import { useState } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { clsx } from 'clsx'

import {
  Card,
  ChannelAvatar,
  DropdownMenu,
  EllipsisVerticalIcon,
  ExternalLinkIcon,
  Heading,
  IconButton,
  SettingsIcon,
  Text,
  useIsMobileScreenSize,
} from '@buffer-mono/popcorn'

import {
  getChannelAdditionalInfo,
  getChannelName,
} from '~publish/helpers/channels'
import { useHasOrganizationAction } from '~publish/contexts/AccountContext'
import { getChannelNameForDisplay } from '~publish/helpers/getChannelNameForDisplay'
import { useRefreshChannelConnection } from '~publish/hooks/useRefreshChannelConnection'
import { channel as channelPageRoute } from '~publish/legacy/routes'
import { canPerform } from '~publish/utils/permissions/canPerform'
import { isViewOnlyPublishChannel } from '~publish/utils/permissions/viewOnlyPublish'

import ChannelDropdownItems from './ChannelDropdownItems'
import { LinkFacebookPageCoachmark } from './LinkFacebookPageCoachmark'
import type { LinkFacebookPageHint } from './LinkFacebookPageCoachmark'
import ChannelStatusSection from './ChannelStatusSection'
import { EditChannelPermissionsDialog } from './EditChannelPermissionsDialog'
import { isStartPage } from '../../shared/utils/isStartPage'
import type { ChannelMask } from '../hooks/useChannelsForSettings'
import { Status, getChannelStatus } from '../utils/getChannelStatus'

import style from './ChannelRow.module.css'
import { isChannelIconService } from '~publish/pages/Community/util/isChannelIconService'

/**
 * Returns channel description text based on the channel type
 * @param channel - The channel to get the descriptor for.
 * @returns The descriptor for the channel.
 */
const getChannelDescriptor = (channel: ChannelMask): string => {
  const isChannelStartPage = isStartPage({ channel })
  if (isChannelStartPage) {
    return 'Start Page'
  }

  let channelDescriptor = channel.descriptor
  if (
    channel.metadata?.__typename === 'MastodonMetadata' ||
    channel.metadata?.__typename === 'BlueskyMetadata'
  ) {
    const additionalInfo = getChannelAdditionalInfo(channel)
    if (additionalInfo) {
      channelDescriptor = `${channelDescriptor} • ${additionalInfo}`
    }
  }
  if (
    channel.metadata?.__typename === 'GoogleBusinessMetadata' ||
    channel.metadata?.__typename === 'FacebookMetadata'
  ) {
    channelDescriptor = `${channelDescriptor} • ${channel.metadata.locationData?.location}`
  }
  return channelDescriptor
}

export interface ChannelRowProps {
  channel: ChannelMask
  onChannelUpdated?: () => void
  /** Points the actor at this row's menu; see {@link LinkFacebookPageCoachmark}. */
  linkFacebookPageHint?: LinkFacebookPageHint
  onLinkFacebookPageHintDismiss?: () => void
}

const ChannelRow = ({
  channel,
  onChannelUpdated,
  linkFacebookPageHint,
  onLinkFacebookPageHintDismiss,
}: ChannelRowProps): JSX.Element => {
  const [isPermissionsDialogOpen, setIsPermissionsDialogOpen] = useState(false)
  const canManageChannels = useHasOrganizationAction('manageChannels')

  const refreshChannelConnection = useRefreshChannelConnection()
  const handleRefreshChannel = (cta: string): void => {
    refreshChannelConnection({
      channel,
      cta,
    })
  }

  const handleOpenPermissionsDialog = (): void => {
    setIsPermissionsDialogOpen(true)
  }

  const handleClosePermissionsDialog = (): void => {
    setIsPermissionsDialogOpen(false)
  }

  const { status } = getChannelStatus({
    channel,
  })
  const channelDescriptor = getChannelDescriptor(channel)
  // A locked row keeps an Unlock or Publish to Connect action, so only a
  // viewer without the manage privilege sees a row with no action at all.
  const isInertLocked = status === Status.Locked && !canManageChannels

  const isMobile = useIsMobileScreenSize()

  let rowActions: ReactNode = null
  if (!canManageChannels) {
    // `manageUpdates` mirrors the ChannelSettings page's own access gate, so
    // the list never links to a settings page that would refuse entry.
    rowActions = (
      <>
        {canPerform(channel, 'manageUpdates') && (
          <IconButton
            label={`Settings for ${getChannelNameForDisplay(channel)}`}
            size="medium"
            variant="tertiary"
            as={Link}
            to={`/channels/${channel.id}/settings`}
          >
            <SettingsIcon />
          </IconButton>
        )}
        {channel.externalLink && (
          <IconButton
            label={
              isStartPage({ channel })
                ? 'View Start Page'
                : `View on ${getChannelName(channel.service)}`
            }
            size="medium"
            variant="tertiary"
            as="a"
            href={channel.externalLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLinkIcon />
          </IconButton>
        )}
      </>
    )
  } else if (isViewOnlyPublishChannel(channel)) {
    rowActions = (
      <IconButton
        label={`View channel ${getChannelNameForDisplay(channel)}`}
        size="medium"
        variant="tertiary"
        as={Link}
        to={channelPageRoute.buildPathname({ id: channel.id })}
      >
        <ExternalLinkIcon />
      </IconButton>
    )
  } else {
    rowActions = (
      <>
        {status !== Status.Locked && !isMobile && (
          <IconButton
            label={`Settings for ${getChannelNameForDisplay(channel)}`}
            size="medium"
            variant="tertiary"
            as={Link}
            to={`/channels/${channel.id}/settings`}
          >
            <SettingsIcon />
          </IconButton>
        )}
        <DropdownMenu modal={false}>
          <LinkFacebookPageCoachmark
            hint={linkFacebookPageHint}
            onDismiss={onLinkFacebookPageHintDismiss ?? ((): void => {})}
          >
            <DropdownMenu.Trigger asChild>
              <IconButton
                label={`More options for ${getChannelNameForDisplay(channel)}`}
                size="medium"
                variant="tertiary"
              >
                <EllipsisVerticalIcon />
              </IconButton>
            </DropdownMenu.Trigger>
          </LinkFacebookPageCoachmark>
          <DropdownMenu.Content align="end">
            <ChannelDropdownItems
              channel={channel}
              status={status}
              refreshChannelConnection={handleRefreshChannel}
              showSettingsLink={isMobile}
              onEditPermissions={handleOpenPermissionsDialog}
              onChannelUpdated={onChannelUpdated}
            />
          </DropdownMenu.Content>
        </DropdownMenu>
      </>
    )
  }

  return (
    <Card
      padding="small"
      className={clsx(style.base, status === Status.Locked && style.locked)}
    >
      {isChannelIconService(channel.service) && (
        <ChannelAvatar
          src={channel.avatar}
          alt={getChannelNameForDisplay(channel)}
          channel={channel.service}
          size="mid"
          className={style.avatar}
        />
      )}
      <div className={style.channelRowCenter}>
        <Heading
          size="xs"
          as="p"
          className={clsx(isInertLocked && style.locked)}
        >
          {getChannelNameForDisplay(channel)}
        </Heading>
        <Text
          color={isInertLocked ? 'disabled' : 'subtle'}
          className={style.channelDescriptor}
        >
          {channelDescriptor}
        </Text>
      </div>
      <div className={style.statusSection}>
        <ChannelStatusSection
          channel={channel}
          status={status}
          refreshChannelConnection={handleRefreshChannel}
        />
      </div>
      <div className={style.channelRowRight}>{rowActions}</div>
      <EditChannelPermissionsDialog
        isOpen={isPermissionsDialogOpen}
        onOpenChange={setIsPermissionsDialogOpen}
        onClose={handleClosePermissionsDialog}
        channel={{
          id: channel.id,
          name: getChannelNameForDisplay(channel),
          avatar: channel.avatar,
          service: channel.service,
        }}
      />
    </Card>
  )
}

export default ChannelRow
